import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Profile } from '../../models/profile';
import { UserLogicService } from '../../services/user.logic.service';
import { EmailService } from '../../services/email.service';
import { ActiveIngredientApiService } from '../../services/echo/active-ingredient.api.service';
import { StepStatus, createStepStatuses } from '../sme-status-section/sme-status-section.component';
import { Constants } from '../../utils/constants';
import { Project } from '../../models/project';
import { ERAPecEccAquaticLogicService } from 'src/app/environmental-risk-assessment/era-pec-ecc/era-pec-ecc-aquatic/services/era-pec-ecc-aquatic.logic.service';
import { EraPecEccAquaticEu } from 'src/app/environmental-risk-assessment/era-pec-ecc/era-pec-ecc-aquatic/era-pec-ecc-aquatic.model';
import { ProjectDeepLinkService } from '../../services/project-deep-link.service';

@Component({
  selector: 'sme-notification',
  templateUrl: './sme-notification.component.html',
  styleUrls: ['./sme-notification.component.css'],
  providers: [MessageService]
})
export class SmeNotificationComponent implements OnInit {
  @Input() selectedProject?: Project;
  @Output() onCloseEvent = new EventEmitter<void>();

  visible: boolean = true;
  suggestions: Profile[] = [];
  selectedRecipients: Profile[] = [];
  notes: string = '';
  sending: boolean = false;
  pecEccAquaticData: EraPecEccAquaticEu[] = [];
  private moleculeNamesByPk = new Map<number, string>();

  stepStatuses: StepStatus[] = createStepStatuses();

  constructor(
    private userLogicService: UserLogicService,
    private emailService: EmailService,
    private messageService: MessageService,
    private pecAquaticLogicService : ERAPecEccAquaticLogicService,
    private deepLinkService: ProjectDeepLinkService,
    private activeIngredientApiService: ActiveIngredientApiService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.selectedProject?.projectPk) {
      this.pecEccAquaticData = (await this.pecAquaticLogicService.getPecEccAquaticEuByProjectPk(this.selectedProject.projectPk))
        .filter(item => item.eraSmeSelection);
      await this.loadActiveIngredientNames();
    }
  }

  private async loadActiveIngredientNames(): Promise<void> {
    const moleculePks = [...new Set((this.selectedProject?.projectXCompoundXModel ?? [])
      .filter(compound => !compound.MetabolitePk)
      .map(compound => compound.MoleculePk)
      .filter((moleculePk): moleculePk is number => moleculePk != undefined))];

    if (moleculePks.length === 0) return;

    const molecules = await firstValueFrom(this.activeIngredientApiService.getMoleculeByMoleculePks(moleculePks));
    this.moleculeNamesByPk = new Map(molecules
      .filter(molecule => molecule.moleculePk != undefined && !!molecule.moleculeName)
      .map(molecule => [molecule.moleculePk!, molecule.moleculeName!]));
  }

  searchUsers(event: AutoCompleteCompleteEvent): void {
    this.userLogicService.searchUsers(event.query).subscribe({
      next: (users) => this.suggestions = users,
      error: () => this.suggestions = []
    });
  }

  get isSendDisabled(): boolean {
    const hasSelectedStep = this.stepStatuses.some(step => step.selected);
    const hasStepError = this.stepStatuses.some(step => !!step.errorMessage);
    return this.selectedRecipients.length === 0 || !hasSelectedStep || hasStepError || this.sending;
  }

  send(): void {
    const recipients = this.selectedRecipients.map(r => r.mail).filter((mail): mail is string => !!mail);
    if (recipients.length === 0) {
      return;
    }

    this.sending = true;
    const subject = `BaMS notification: the PECs values in Project ${this.selectedProject?.name} have been approved`;
    const body = this.buildEmailBody();

    this.emailService.sendMail(recipients, subject, body).subscribe({
      next: () => {
        this.sending = false;
        this.messageService.add({ severity: 'success', summary: 'Notification sent', detail: 'The SME notification email was sent successfully.' });
        this.close();
      },
      error: () => {
        this.sending = false;
        this.messageService.add({ severity: 'error', summary: 'Send failed', detail: 'The SME notification email could not be sent.' });
      }
    });
  }

  // Inline CSS is required since most email clients strip external/embedded stylesheets
  private static readonly EMAIL_STYLES = {
    container: 'font-family: Arial, Helvetica, sans-serif; color: #333333; font-size: 12px; max-width: 700px;',
    heading: 'font-size: 13px; color: #333333; border-bottom: 2px solid #F59E0B; padding-bottom: 4px; margin: 16px 0 8px;',
    paragraph: 'margin: 0 0 12px; line-height: 1.5;',
    notesBox: 'background-color: #fff8e1; border-left: 4px solid #F59E0B; padding: 8px 12px; margin-bottom: 12px;',
    tableWrapper: 'overflow-x: auto; margin-bottom: 12px;',
    table: 'width: auto; border-collapse: collapse; font-size: 11px; table-layout: auto;',
    th: 'background-color: #F59E0B; color: #333333; text-align: left; padding: 5px; border: 1px solid #dddddd; white-space: nowrap;',
    td: 'padding: 5px; border: 1px solid #dddddd; word-break: break-word; white-space: nowrap;',
    linkList: 'margin: 0 0 12px; padding-left: 18px; line-height: 1.5;',
    link: 'color: #0069be; text-decoration: underline;'
  };

  private buildEmailBody(): string {
    const styles = SmeNotificationComponent.EMAIL_STYLES;
    const notesHtml = this.notes
      ? `<div style="${styles.notesBox}"><p style="margin: 0 0 6px; font-weight: bold;">Notes</p><p style="margin: 0;">${this.escapeHtml(this.notes)}</p></div>`
      : '';
    const activesTable = this.buildActiveIngredientsModelsTable();
    const applicationSchemes = this.buildApplicationSchemes();
    return `
      <div style="${styles.container}">
        ${notesHtml}
        <h3 style="${styles.heading}">Project details</h3>
        <p style="${styles.paragraph}">
          <strong>Project number:</strong> ${this.selectedProject?.projectPk ?? ''}<br>
          <strong>Project name:</strong> ${this.escapeHtml(this.selectedProject?.name)}<br>
          <strong>Project description:</strong> ${this.escapeHtml(this.selectedProject?.description)}<br>
          <strong>Formulation:</strong> ${this.escapeHtml(this.selectedProject?.formulation)}
        </p>
        ${this.buildEraLinks()}
        ${activesTable}
        ${applicationSchemes}
      </div>`;
  }

  private buildEraLinks(): string {
    const { heading, linkList, link } = SmeNotificationComponent.EMAIL_STYLES;
    const projectPk = this.selectedProject?.projectPk;
    if (projectPk == undefined) return '';

    const route = Constants.DEEP_LINK_ROUTES.ENVIRONMENTAL_RISK_ASSESSMENT;
    const endpointsUrl = this.deepLinkService.buildLink(route, projectPk, Constants.RISK_ASSESSMENT_STEP_INDEXES.ECOTOX_ENDPOINTS);
    const runRiskAssessmentUrl = this.deepLinkService.buildLink(route, projectPk, Constants.RISK_ASSESSMENT_STEP_INDEXES.RUN_RISK_ASSESSMENT);

    return `
      <h3 style="${heading}">Next steps</h3>
      <ol style="${linkList}">
        <li>
          Go to ERA, select the project, go to the <strong>Ecotox Endpoints</strong> section and enter the ecotox endpoints
          for the formulation and the actives (one dataset for each). Shortcut: <a style="${link}" href="${endpointsUrl}">Click here</a>
        </li>
        <li>
          Go to the <strong>Run Risk Assessment/</strong> Mix Tox Calculator Section
          and click All Files Button. Shortcut: <a style="${link}" href="${runRiskAssessmentUrl}">Click here</a>
        </li>
      </ol>`;
  }

  private escapeHtml(value?: string): string {
    return (value ?? '').replace(/[&<>"']/g, char => `&#${char.charCodeAt(0)};`);
  }

  private renderTable(title: string, headers: string[], rows: string[]): string {
    const { heading, tableWrapper, table, th } = SmeNotificationComponent.EMAIL_STYLES;
    const headerCells = headers.map(header => `<th style="${th}">${header}</th>`).join('');
    return `
      <h3 style="${heading}">${title}</h3>
      <div style="${tableWrapper}">
        <table style="${table}">
          <tr>${headerCells}</tr>
          ${rows.join('')}
        </table>
      </div>`;
  }

  private buildActiveIngredientsModelsTable(): string {
    const activeIngredients = (this.selectedProject?.projectXCompoundXModel ?? [])
      .filter(c => !c.MetabolitePk);

    const modelsByActive = new Map<string, Set<string>>();
    activeIngredients.forEach(c => {
      const activeName = c.AlternativeNameCompound || (c.MoleculePk != undefined ? this.moleculeNamesByPk.get(c.MoleculePk) : undefined);
      if (!activeName) {
        return;
      }
      if (!modelsByActive.has(activeName)) {
        modelsByActive.set(activeName, new Set<string>());
      }
      if (c.ModelName) {
        modelsByActive.get(activeName)!.add(c.ModelName);
      }
    });

    const { td } = SmeNotificationComponent.EMAIL_STYLES;
    const rows = [...modelsByActive.entries()]
      .map(([activeName, models]) => `<tr><td style="${td}">${this.escapeHtml(activeName)}</td><td style="${td}">${this.escapeHtml([...models].join(', '))}</td></tr>`);

    return this.renderTable('Active ingredients & models', ['Active', 'Models'], rows);
  }

  private buildApplicationSchemes(): string {
    const { heading, linkList } = SmeNotificationComponent.EMAIL_STYLES;
    const stepLabels = this.stepStatuses.map(step => step.label);
    const schemes = [...new Set(this.pecEccAquaticData
      .filter(item => item.scenarioFocus && stepLabels.includes(item.scenarioFocus))
      .map(item => item.applicationScheme)
      .filter((scheme): scheme is string => !!scheme))];

    if (!schemes.length) return '';

    return `
      <h3 style="${heading}">Application Schemes</h3>
      <ul style="${linkList}">
        ${schemes.map(scheme => `<li>${this.escapeHtml(scheme)}</li>`).join('')}
      </ul>`;
  }

  close(): void {
    this.onCloseEvent.emit();
  }
}
